import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWmsOrdenes, getWmsAlmacenes } from '../../api.wms';
import { getWmsClientes } from '../../api.wms.clientes';
import { getWmsProductos, getWmsProductosByIds } from '../../api.wms.productos';
import WmsSidebar from '../../components/WmsSidebar';
import WmsTopbar from '../../components/wms_topbar';
import { Eye, Trash2, FileText, X, MoreVertical, ChevronDown, ChevronRight } from 'lucide-react';
import ReactDOM from 'react-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import OrdenEntradaPDF from '../../components/OrdenEntradaPDF';

const WmsOrdenesConsulta = () => {
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [modalProductos, setModalProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [ordenEdit, setOrdenEdit] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedProductos, setExpandedProductos] = useState([]);
  const [pdfOrden, setPdfOrden] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    const fetchOrdenes = async () => {
      try {
        const data = await getWmsOrdenes();
        setOrdenes(data);
      } catch (err) {
        setError('Error al cargar las órdenes');
      } finally {
        setLoading(false);
      }
    };
    fetchOrdenes();
  }, []);

  useEffect(() => {
    getWmsAlmacenes().then(setAlmacenes);
    getWmsClientes().then(setClientes);
    getWmsProductos().then(setProductos);
  }, []);

  const getAlmacenNombre = (id) => {
    const a = almacenes.find(x => x.id === id);
    return a ? a.nombre : id;
  };
  const getClienteNombre = (id) => {
    const c = clientes.find(x => x.id === id);
    return c ? c.nombre : id;
  };
  const getProductoNombre = (id) => {
    const p = productos.find(x => x.id === id);
    return p ? p.descripcion : id;
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Seguro que deseas eliminar esta orden?')) {
      setOrdenes(ordenes.filter(o => o.id !== id));
      // Aquí podrías llamar a la API para eliminar en backend
    }
  };
  const handleView = async (orden) => {
    setOrdenEdit(JSON.parse(JSON.stringify(orden)));
    setModalOpen(true);
    // Obtener los productos completos solo si hay productos
    if (orden.productos && orden.productos.length > 0) {
      const ids = orden.productos.map(p => p.producto_id);
      const productosCompletos = await getWmsProductosByIds(ids);
      setModalProductos(productosCompletos);
    } else {
      setModalProductos([]);
    }
  };
  const handleModalChange = (field, value) => {
    setOrdenEdit({ ...ordenEdit, [field]: value });
  };
  const handleProductoChange = (idx, field, value) => {
    const nuevos = ordenEdit.productos.map((p, i) =>
      i === idx ? { ...p, [field]: value } : p
    );
    setOrdenEdit({ ...ordenEdit, productos: nuevos });
  };
  const handleModalSave = () => {
    setOrdenes(ordenes.map(o => o.id === ordenEdit.id ? ordenEdit : o));
    setModalOpen(false);
  };
  const handleDownloadPDF = (orden) => {
    if (orden.tipo === 'entrada') {
      // Mapear productos para asegurar que tengan sku, descripcion y unidad
      const productosMapeados = (orden.productos || []).map(p => {
        // Buscar en el catálogo global de productos
        const prodCat = productos.find(prod => prod.id === (p.producto_id || p.producto));
        return {
          ...p,
          sku: p.sku || (prodCat ? prodCat.codigo_producto : ''),
          descripcion: p.descripcion || (prodCat ? prodCat.descripcion : ''),
          unidad: p.unidad || (prodCat ? prodCat.unidad_medida : ''),
        };
      });
      setPdfOrden({ ...orden, productos: productosMapeados });
      setPdfReady(true);
    } else {
      alert('Solo disponible para órdenes de entrada');
    }
  };

  const handleOpenMenu = (id, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setOpenMenu(id);
  };
  const handleCloseMenu = () => setOpenMenu(null);

  const handleExpandRow = async (orden) => {
    if (expandedRow === orden.id) {
      setExpandedRow(null);
      setExpandedProductos([]);
    } else {
      setExpandedRow(orden.id);
      if (orden.productos && orden.productos.length > 0) {
        const ids = orden.productos.map(p => p.producto_id);
        const productosCompletos = await getWmsProductosByIds(ids);
        setExpandedProductos(productosCompletos);
      } else {
        setExpandedProductos([]);
      }
    }
  };

  // Badge visual para el estado
  const getEstadoBadge = (estado) => {
    let color = 'bg-gray-200 text-gray-700';
    if (estado === 'pendiente') color = 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    if (estado === 'completado' || estado === 'finalizado') color = 'bg-emerald-100 text-emerald-800 border border-emerald-300';
    if (estado === 'cancelado') color = 'bg-red-100 text-red-700 border border-red-300';
    if (estado === 'en proceso' || estado === 'procesando') color = 'bg-blue-100 text-blue-800 border border-blue-300';
    return (
      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${color}`} style={{minWidth: 80, textAlign: 'center'}}>
        {estado.charAt(0).toUpperCase() + estado.slice(1)}
      </span>
    );
  };

  const getProductoCatalogo = (producto_id) => modalProductos.find(p => p.id === producto_id) || {};
  const getProductoCatalogoExpand = (producto_id) => expandedProductos.find(p => p.id === producto_id) || {};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.actions-modal') && !event.target.closest('.actions-button')) {
        handleCloseMenu();
      }
    };
    if (openMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenu]);

  const ordenMenu = openMenu !== null ? ordenes.find(o => o.id === openMenu) : null;

  useEffect(() => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${pdfOrden.iddocumento}.pdf`;
      link.click();
      setPdfReady(false);
      setPdfUrl(null);
      setPdfOrden(null);
    }
  }, [pdfUrl]);

  return (
    <div className="flex">
      <WmsSidebar activeItem="ordenes" setActiveItem={() => {}} />
      <div className="transition-all duration-300 flex-1 bg-gray-100 min-h-screen p-0 flex flex-col items-center">
        <div className="w-full sticky top-0 z-20 bg-gray-100">
          <WmsTopbar />
        </div>
        <div className="w-full px-2 sm:px-6 flex flex-col items-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-2 text-center mt-4">Consulta de Órdenes</h1>
          <p className="mb-6 text-center text-gray-700 text-base sm:text-lg">Aquí podrás consultar las órdenes creadas en el sistema.</p>
          <div className="w-full flex justify-end">
            <button
              className="mb-4 px-4 py-1 bg-transparent hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg font-medium text-sm transition-all shadow-none"
              onClick={() => navigate('/wms/ordenes')}
            >
              ← Regresar
            </button>
          </div>
          <div className="p-8 w-full max-w-5xl mx-auto">
            <h1 className="text-2xl font-bold mb-6 text-neutral-800">Órdenes WMS</h1>
            {loading && <div>Cargando...</div>}
            {error && <div className="text-red-600">{error}</div>}
            {!loading && !error && (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-neutral-200 rounded-lg bg-white text-sm">
                  <thead className="bg-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-1 py-2"></th>
                      <th className="px-3 py-2 text-left">ID Documento</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-left">Almacén</th>
                      <th className="px-3 py-2 text-left">Cliente</th>
                      <th className="px-3 py-2 text-left">Referencia</th>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-left">Productos</th>
                      <th className="px-3 py-2 text-left">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordenes.length === 0 && (
                      <tr>
                        <td colSpan="9" className="text-center text-neutral-400 py-4">No hay órdenes registradas</td>
                      </tr>
                    )}
                    {ordenes.map((orden, idx) => (
                      <React.Fragment key={orden.id}>
                        <tr className={idx % 2 === 0 ? "bg-white" : "bg-neutral-50"} style={{ transition: 'background 0.2s', height: 56 }}>
                          <td className="px-1 py-2 text-center align-middle">
                            <button
                              className="p-1 rounded hover:bg-gray-100"
                              onClick={() => handleExpandRow(orden)}
                              title={expandedRow === orden.id ? 'Ocultar productos' : 'Ver productos'}
                              type="button"
                            >
                              {expandedRow === orden.id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                            </button>
                          </td>
                          <td className="px-3 py-2 font-mono">{orden.iddocumento}</td>
                          <td className="px-3 py-2">{orden.tipo}</td>
                          <td className="px-3 py-2">{getAlmacenNombre(orden.almacen_id)}</td>
                          <td className="px-3 py-2">{getClienteNombre(orden.cliente_id)}</td>
                          <td className="px-3 py-2">{orden.referencia}</td>
                          <td className="px-3 py-2">{orden.fecha}</td>
                          <td className="px-3 py-2">{getEstadoBadge(orden.estado)}</td>
                          <td className="px-3 py-2">
                            {orden.productos && orden.productos.length > 0
                              ? `${orden.productos.length} producto${orden.productos.length > 1 ? 's' : ''}`
                              : <span className="text-neutral-400">Sin productos</span>}
                          </td>
                          <td className="px-3 py-2 flex items-center justify-center relative">
                            <button
                              className="p-2 rounded-full hover:bg-gray-100 text-gray-700 focus:outline-none"
                              onClick={e => handleOpenMenu(orden.id, e)}
                              title="Más acciones"
                              type="button"
                            >
                              <MoreVertical className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                        {expandedRow === orden.id && (
                          <tr className="bg-neutral-50">
                            <td colSpan="10" className="px-6 py-4">
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-xs border border-neutral-200 rounded-lg bg-white">
                                  <thead className="bg-neutral-100 sticky top-0 z-10">
                                    <tr className="text-neutral-700 font-semibold text-xs uppercase tracking-wide">
                                      <th className="px-3 py-2 text-left">SKU</th>
                                      <th className="px-3 py-2 text-left">Descripción</th>
                                      <th className="px-3 py-2 text-left">UM</th>
                                      <th className="px-3 py-2 text-left">Cantidad</th>
                                      <th className="px-3 py-2 text-left">Lote</th>
                                      <th className="px-3 py-2 text-left">Caducidad</th>
                                      <th className="px-3 py-2 text-left">Serie</th>
                                      <th className="px-3 py-2 text-left">Calidad</th>
                                      <th className="px-3 py-2 text-left">Comentario</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {orden.productos && orden.productos.length > 0 ? (
                                      orden.productos.map((p, idx2) => {
                                        const prodCat = getProductoCatalogoExpand(p.producto_id);
                                        const calidadColor =
                                          p.calidad === 'A' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                                          p.calidad === 'B' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                          p.calidad === 'C' ? 'bg-red-100 text-red-700 border border-red-200' :
                                          'bg-gray-100 text-gray-700 border border-gray-200';
                                        return (
                                          <tr key={p.id} className={idx2 % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} style={{ transition: 'background 0.2s' }}>
                                            <td className="px-3 py-2 font-mono text-blue-800">{prodCat.codigo_producto || p.sku || p.producto_id || '—'}</td>
                                            <td className="px-3 py-2 text-neutral-700">{prodCat.descripcion || p.descripcion || '—'}</td>
                                            <td className="px-3 py-2">{prodCat.unidad_medida || p.unidad || '—'}</td>
                                            <td className="px-3 py-2">{p.cantidad || '—'}</td>
                                            <td className="px-3 py-2">{p.lote || '—'}</td>
                                            <td className="px-3 py-2">{p.caducidad || '—'}</td>
                                            <td className="px-3 py-2">{p.serie || '—'}</td>
                                            <td className="px-3 py-2">
                                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${calidadColor}`}>{p.calidad || '—'}</span>
                                            </td>
                                            <td className="px-3 py-2 text-neutral-600">{p.comentario || '—'}</td>
                                          </tr>
                                        );
                                      })
                                    ) : (
                                      <tr><td colSpan="9" className="text-center text-neutral-400 py-2">Sin productos</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {modalOpen && ordenEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl relative border border-neutral-200">
            <button className="absolute top-2 right-2 text-neutral-400 hover:text-neutral-700" onClick={() => setModalOpen(false)}>
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold mb-4">Ver/Editar Orden</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">ID Documento</label>
                <input type="text" value={ordenEdit.iddocumento} disabled className="w-full border border-neutral-300 rounded-md px-3 py-2 bg-neutral-100 text-neutral-800" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Referencia</label>
                <input type="text" value={ordenEdit.referencia || ''} onChange={e => handleModalChange('referencia', e.target.value)} className="w-full border border-neutral-300 rounded-md px-3 py-2 bg-white text-neutral-800" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Estado</label>
                <input type="text" value={ordenEdit.estado} disabled className="w-full border border-neutral-300 rounded-md px-3 py-2 bg-neutral-100 text-neutral-800" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Fecha de creación</label>
                <input type="text" value={ordenEdit.fecha} disabled className="w-full border border-neutral-300 rounded-md px-3 py-2 bg-neutral-100 text-neutral-800" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Productos</label>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border border-neutral-200 rounded-lg bg-white">
                  <thead>
                    <tr className="bg-neutral-100 text-neutral-700 font-semibold text-xs uppercase tracking-wide">
                      <th className="px-3 py-2 text-left">SKU</th>
                      <th className="px-3 py-2 text-left">Descripción</th>
                      <th className="px-3 py-2 text-left">UM</th>
                      <th className="px-3 py-2 text-left">Cantidad</th>
                      <th className="px-3 py-2 text-left">Lote</th>
                      <th className="px-3 py-2 text-left">Caducidad</th>
                      <th className="px-3 py-2 text-left">Serie</th>
                      <th className="px-3 py-2 text-left">Calidad</th>
                      <th className="px-3 py-2 text-left">Comentario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordenEdit.productos && ordenEdit.productos.length > 0 ? (
                      ordenEdit.productos.map((p, idx) => (
                        <tr key={p.id}>
                          <td className="px-3 py-2 font-mono">{getProductoCatalogo(p.producto_id).codigo_producto || p.sku || p.producto_id}</td>
                          <td className="px-3 py-2">{getProductoCatalogo(p.producto_id).descripcion || p.descripcion || p.producto_id}</td>
                          <td className="px-3 py-2">{getProductoCatalogo(p.producto_id).unidad_medida || p.unidad || ''}</td>
                          <td className="px-3 py-2">
                            <input type="number" min="1" value={p.cantidad} onChange={e => handleProductoChange(idx, 'cantidad', e.target.value)} className="w-20 border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-800" />
                          </td>
                          <td className="px-3 py-2">
                            <input type="text" value={p.lote || ''} onChange={e => handleProductoChange(idx, 'lote', e.target.value)} className="w-20 border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-800" />
                          </td>
                          <td className="px-3 py-2">
                            <input type="date" value={p.caducidad || ''} onChange={e => handleProductoChange(idx, 'caducidad', e.target.value)} className="w-32 border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-800" />
                          </td>
                          <td className="px-3 py-2">
                            <input type="text" value={p.serie || ''} onChange={e => handleProductoChange(idx, 'serie', e.target.value)} className="w-20 border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-800" />
                          </td>
                          <td className="px-3 py-2">
                            <select value={p.calidad || 'A'} onChange={e => handleProductoChange(idx, 'calidad', e.target.value)} className="border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-800">
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input type="text" value={p.comentario || ''} onChange={e => handleProductoChange(idx, 'comentario', e.target.value)} className="w-32 border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-800" />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="9" className="text-center text-neutral-400 py-2">Sin productos</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button className="px-4 py-2 rounded bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-semibold" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="px-4 py-2 rounded bg-blue-700 hover:bg-blue-800 text-white font-semibold" onClick={handleModalSave}>Guardar</button>
            </div>
          </div>
        </div>
      )}
      {openMenu !== null && ordenMenu && (
        ReactDOM.createPortal(
          <div
            className="fixed z-50 actions-modal p-2 flex flex-col gap-1 bg-white border rounded shadow-lg"
            style={{ top: menuPosition.top, left: menuPosition.left, minWidth: 160 }}
          >
            <button
              className="flex items-center gap-2 w-full text-left px-3 py-2 rounded hover:bg-blue-50 text-neutral-800"
              onClick={() => { handleView(ordenMenu); handleCloseMenu(); }}
            >
              <Eye className="w-4 h-4" /> Ver / Editar
            </button>
            <button
              className="flex items-center gap-2 w-full text-left px-3 py-2 rounded hover:bg-emerald-50 text-neutral-800"
              onClick={() => { handleDownloadPDF(ordenMenu); handleCloseMenu(); }}
            >
              <FileText className="w-4 h-4" /> Descargar PDF
            </button>
            <button
              className="flex items-center gap-2 w-full text-left px-3 py-2 rounded hover:bg-red-50 text-neutral-800"
              onClick={() => { handleDelete(openMenu); handleCloseMenu(); }}
            >
              <Trash2 className="w-4 h-4" /> Eliminar
            </button>
          </div>,
          document.body
        )
      )}
      {pdfOrden && pdfReady && (
        <div style={{ display: 'none' }}>
          <PDFDownloadLink
            document={<OrdenEntradaPDF orden={pdfOrden} logoSrc="/logoamer.ico" />}
            fileName={`${pdfOrden.iddocumento}.pdf`}
          >
            {({ url, loading }) => {
              if (!loading && url && pdfReady && !pdfUrl) {
                setPdfUrl(url);
              }
              return null;
            }}
          </PDFDownloadLink>
        </div>
      )}
    </div>
  );
};

export default WmsOrdenesConsulta;
